import type { MetaDatePreset } from 'utils/metaDatePresets';

import baseWorkloadApi from './baseWorkloadApi';

export interface MetaInsight {
  date_start: string;
  date_stop: string;
  impressions?: string;
  clicks?: string;
  spend?: string;
  reach?: string;
  frequency?: string;
  cpm?: string;
  cpc?: string;
  ctr?: string;
  cpp?: string;
  cost_per_unique_click?: string;
  unique_clicks?: string;
  unique_ctr?: string;
  adset_id?: string;
  adset_name?: string;
  ad_id?: string;
  ad_name?: string;
}

export interface MetaCampaignWithInsights {
  id: string;
  name: string;
  status: string;
  isActive: boolean;
  startTime: string | null;
  objective: string | null;
  insights: MetaInsight | null;
}

export interface MetaAdAccountSummary {
  id: number;
  metaAdAccountId: string;
  name: string;
  isActive: boolean;
  currency: string;
}

export interface MetaCampaignsAccountBlock {
  metaAdAccount: MetaAdAccountSummary;
  metaConnectionId: number;
  datePreset: MetaDatePreset | null;
  campaigns: MetaCampaignWithInsights[];
}

/**
 * Normalized shape returned by `useGetMetaCampaignsWithInsightsQuery`.
 * Single-account queries map to a 1-element `results` array so consumers can always iterate.
 * `datePreset` and `since`/`until` may be null depending on which time range form the caller used.
 */
export interface MetaCampaignsWithInsightsResponse {
  results: MetaCampaignsAccountBlock[];
  datePreset: MetaDatePreset | null;
  since: string | null;
  until: string | null;
}

/** Server response shape when a single `metaAdAccountId` is sent — backward-compat. */
type MetaCampaignsWithInsightsSingleResponse = MetaCampaignsAccountBlock;

/** Server response shape when `metaAdAccountIds` is sent — the multi-account form. */
interface MetaCampaignsWithInsightsMultiResponse {
  results: MetaCampaignsAccountBlock[];
  datePreset: MetaDatePreset | null;
  since: string | null;
  until: string | null;
}

type RawMetaCampaignsWithInsightsResponse =
  | MetaCampaignsWithInsightsSingleResponse
  | MetaCampaignsWithInsightsMultiResponse;

/**
 * Lead-level metrics attached to each adset or ad row when the drill-down endpoints are
 * called with a `businessUnitId` + `since`/`until`. Null when those params weren't sent.
 */
export interface MetaLeadsStats {
  leadsWithClientResponses: number;
  leadsWithHighMessageCount: number;
  leadsUploadedToCRM: number;
}

export interface MetaAdSetWithInsights {
  id: string;
  name: string;
  status: string;
  isActive: boolean;
  startTime: string | null;
  optimizationGoal: string | null;
  destinationType: string | null;
  insights: MetaInsight | null;
  leadsStats: MetaLeadsStats | null;
}

interface MetaAdSetsWithInsightsResponse {
  campaignId: string;
  datePreset: MetaDatePreset | null;
  since: string | null;
  until: string | null;
  adSets: MetaAdSetWithInsights[];
}

export interface MetaAdWithInsights {
  id: string;
  name: string | null;
  status: string | null;
  isActive: boolean | null;
  insights: MetaInsight | null;
  leadsStats: MetaLeadsStats | null;
}

interface MetaAdsWithInsightsResponse {
  adSetId: string;
  datePreset: MetaDatePreset | null;
  since: string | null;
  until: string | null;
  ads: MetaAdWithInsights[];
}

interface GetAdSetsWithInsightsArgs {
  metaConnectionId: number;
  campaignId: string;
  /** Either `datePreset` or both `since`/`until` must be provided. */
  datePreset?: MetaDatePreset;
  since?: string;
  until?: string;
  /** When provided with a custom date range, lead stats per adset are included in the response. */
  businessUnitId?: number;
  fields?: string[];
}

interface GetAdsWithInsightsArgs {
  metaConnectionId: number;
  adSetId: string;
  datePreset?: MetaDatePreset;
  since?: string;
  until?: string;
  businessUnitId?: number;
  fields?: string[];
}

interface GetMetaCampaignsWithInsightsArgs {
  /** Single ad account external ID (e.g. "act_123"). Mutually optional with `metaAdAccountIds`. */
  metaAdAccountId?: string;
  /** Multiple ad account external IDs — one query fans out server-side. */
  metaAdAccountIds?: string[];
  /** Meta date preset (e.g. "last_30d"). Ignored when `since`/`until` are both set. */
  datePreset?: MetaDatePreset;
  /** Custom time range start in YYYY-MM-DD. Requires `until`. Overrides `datePreset`. */
  since?: string;
  /** Custom time range end in YYYY-MM-DD. Requires `since`. Overrides `datePreset`. */
  until?: string;
  isActive?: boolean;
  fields?: string[];
}

interface ExportMetaInsightsCsvArgs {
  metaAdAccountId: string;
  datePreset: MetaDatePreset;
  businessUnitId: number;
  startDate: string;
  endDate: string;
  isActive?: boolean;
}

const metaInsightsApi = baseWorkloadApi.injectEndpoints({
  endpoints: (build) => ({
    getMetaCampaignsWithInsights: build.query<MetaCampaignsWithInsightsResponse, GetMetaCampaignsWithInsightsArgs>({
      query: ({ metaAdAccountId, metaAdAccountIds, datePreset, since, until, isActive, fields }) => ({
        url: '/metaInsights/meta/campaigns-with-insights',
        method: 'GET',
        params: {
          ...(metaAdAccountIds && metaAdAccountIds.length > 0
            ? { metaAdAccountIds: metaAdAccountIds.join(',') }
            : metaAdAccountId
              ? { metaAdAccountId }
              : {}),
          ...(since && until ? { since, until } : datePreset ? { datePreset } : {}),
          ...(isActive !== undefined ? { isActive } : {}),
          ...(fields && fields.length > 0 ? { fields: fields.join(',') } : {}),
        },
      }),
      transformResponse: (response: RawMetaCampaignsWithInsightsResponse): MetaCampaignsWithInsightsResponse => {
        if ('results' in response) {
          return {
            results: response.results,
            datePreset: response.datePreset ?? null,
            since: response.since ?? null,
            until: response.until ?? null,
          };
        }
        return {
          results: [response],
          datePreset: response.datePreset ?? null,
          since: null,
          until: null,
        };
      },
    }),
    getMetaAdSetsWithInsights: build.query<MetaAdSetsWithInsightsResponse, GetAdSetsWithInsightsArgs>({
      query: ({ metaConnectionId, campaignId, datePreset, since, until, businessUnitId, fields }) => ({
        url: `/metaInsights/meta/campaign/${campaignId}/adsets-with-insights`,
        method: 'GET',
        params: {
          metaConnectionId,
          ...(since && until ? { since, until } : datePreset ? { datePreset } : {}),
          ...(businessUnitId ? { businessUnitId } : {}),
          ...(fields && fields.length > 0 ? { fields: fields.join(',') } : {}),
        },
      }),
      transformResponse: (response: MetaAdSetsWithInsightsResponse) => response,
    }),
    getMetaAdsWithInsights: build.query<MetaAdsWithInsightsResponse, GetAdsWithInsightsArgs>({
      query: ({ metaConnectionId, adSetId, datePreset, since, until, businessUnitId, fields }) => ({
        url: `/metaInsights/meta/adset/${adSetId}/ads-with-insights`,
        method: 'GET',
        params: {
          metaConnectionId,
          ...(since && until ? { since, until } : datePreset ? { datePreset } : {}),
          ...(businessUnitId ? { businessUnitId } : {}),
          ...(fields && fields.length > 0 ? { fields: fields.join(',') } : {}),
        },
      }),
      transformResponse: (response: MetaAdsWithInsightsResponse) => response,
    }),
    exportMetaInsightsCsv: build.query<Blob, ExportMetaInsightsCsvArgs>({
      query: ({ metaAdAccountId, datePreset, businessUnitId, startDate, endDate, isActive }) => ({
        url: '/metaInsights/meta/export-csv',
        method: 'GET',
        params: {
          metaAdAccountId,
          datePreset,
          businessUnitId,
          startDate,
          endDate,
          ...(isActive !== undefined ? { isActive } : {}),
        },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetMetaCampaignsWithInsightsQuery,
  useLazyGetMetaAdSetsWithInsightsQuery,
  useLazyGetMetaAdsWithInsightsQuery,
  useLazyExportMetaInsightsCsvQuery,
} = metaInsightsApi;
